import React, { useEffect, useMemo, useState } from 'react';
import { DatePicker, Popover } from 'antd';
import { Calendar, Check, ChevronDown, ListFilter, X } from 'lucide-react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import dayjs from '@tt/dates/dayjs';

import { Button as UIButton } from '../../../../components/ui/button';
import { cn } from '../../../../lib/utils';
import {
    useAccountStore,
    useManagementStore,
    useSettingStore,
    useUserState
} from '../../../../store';
import { getCompanyFromAccountUniqueId } from '../../../../utilities/account-ids';
import { SUMMARY } from '../../../../utilities/functions';

import {
    ASSET_OPTIONS,
    COMPANY_ACCOUNT_FILTERS,
    RANGE_PRESETS,
    SIDES,
    STATUSES
} from './constants';
import { useIsViewAsActive, useSymbols, useTags, useTradingPlans } from './hooks';
import TradebookFilters from './TradebookFilters.jsx';

import './styles.scss';

const { RangePicker } = DatePicker;
const SHEET_TRANSITION_MS = 320;

const SHEET_FILTER_KEYS = [
    'basic_measure',
    'assetType',
    'tags',
    'showAllTags',
    'symbols',
    'side',
    'status',
    'tradingPlanId',
    'strategySetType',
    'setNumber'
];

const DEFAULT_ASSET_TYPES = ASSET_OPTIONS.map((option) => option.value);
const DEFAULT_SIDES = [...SIDES];
const MEASURE_OPTIONS = [
    { label: '$ - Dollar', value: 'Dollar' },
    { label: 'R - R-multiple', value: 'R-multiple' }
];
const STATUS_OPTIONS = STATUSES.map((status) => ({
    label: status,
    value: status.toLowerCase()
}));
const DEFAULT_STATUSES = STATUS_OPTIONS.map((option) => option.value);

const areArraysEqual = (first = [], second = []) => {
    if (!Array.isArray(first) || !Array.isArray(second)) return false;
    if (first.length !== second.length) return false;

    const sortedFirst = [...first].sort();
    const sortedSecond = [...second].sort();

    return sortedFirst.every((item, index) => item === sortedSecond[index]);
};

const createFilterDraft = (filter) => ({
    ...filter,
    assetType: Array.isArray(filter.assetType) ? [...filter.assetType] : [...DEFAULT_ASSET_TYPES],
    tags: Array.isArray(filter.tags) ? [...filter.tags] : [],
    symbols: Array.isArray(filter.symbols) ? [...filter.symbols] : [],
    side: Array.isArray(filter.side) ? [...filter.side] : [...DEFAULT_SIDES],
    status: Array.isArray(filter.status) ? [...filter.status] : [...DEFAULT_STATUSES]
});

const createResetDraft = (filter, uniqueSymbols) => ({
    ...filter,
    basic_measure: 'Dollar',
    assetType: [...DEFAULT_ASSET_TYPES],
    tags: [],
    showAllTags: false,
    symbols: [...uniqueSymbols],
    side: [...DEFAULT_SIDES],
    status: [...DEFAULT_STATUSES],
    tradingPlanId: null,
    strategySetType: null,
    setNumber: null
});

const summarizeValues = ({
    values,
    labelByValue,
    placeholder = 'Select',
    allValues = [],
    treatAllAsPlaceholder = false,
    allSelectedLabel = null,
    maxVisible = 2
}) => {
    if (!Array.isArray(values) || values.length === 0) return placeholder;

    if (allValues.length > 0 && areArraysEqual(values, allValues)) {
        if (treatAllAsPlaceholder) return placeholder;
        if (allSelectedLabel) return allSelectedLabel;
    }

    const labels = values.map((value) => labelByValue[value] || value).filter(Boolean);

    if (labels.length <= maxVisible) return labels.join(', ');

    return `${labels.slice(0, maxVisible).join(', ')} +${labels.length - maxVisible}`;
};

function SheetField({ label, children }) {
    return (
        <div className="space-y-2">
            <p className="text-sm font-medium leading-5 text-[#0A0A0A]">{label}</p>
            {children}
        </div>
    );
}

SheetField.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired
};

function SelectionIndicator({ checked }) {
    return (
        <span
            className={cn(
                'flex h-4 w-4 items-center justify-center rounded border transition-colors',
                checked
                    ? 'border-[#2563EB] bg-[#2563EB] text-white'
                    : 'border-[#D4D4D8] bg-white text-transparent'
            )}
        >
            <Check className="h-3 w-3" />
        </span>
    );
}

SelectionIndicator.propTypes = {
    checked: PropTypes.bool.isRequired
};

function CheckboxListPanel({
    allSelected,
    emptyText,
    onClear,
    onSelectAllToggle,
    options,
    selectedValues,
    title,
    onToggleValue
}) {
    return (
        <div className="journal-filter-popover">
            <div className="journal-filter-popover__header">
                <span className="journal-filter-popover__title">{title}</span>
                <div className="journal-filter-popover__actions">
                    <button
                        className="journal-filter-popover__action"
                        type="button"
                        onClick={onClear}
                    >
                        Clear
                    </button>
                    <button
                        className="journal-filter-popover__action"
                        type="button"
                        onClick={onSelectAllToggle}
                    >
                        {allSelected ? 'Deselect all' : 'Select all'}
                    </button>
                </div>
            </div>
            <div className="journal-filter-popover__list">
                {options.length > 0 ? (
                    options.map((option) => {
                        const checked = selectedValues.includes(option.value);

                        return (
                            <button
                                key={option.value}
                                className="journal-filter-popover__option"
                                type="button"
                                onClick={() => onToggleValue(option.value)}
                            >
                                <SelectionIndicator checked={checked} />
                                <span className="truncate">{option.label}</span>
                            </button>
                        );
                    })
                ) : (
                    <p className="journal-filter-popover__empty">{emptyText}</p>
                )}
            </div>
        </div>
    );
}

CheckboxListPanel.propTypes = {
    allSelected: PropTypes.bool.isRequired,
    emptyText: PropTypes.string.isRequired,
    onClear: PropTypes.func.isRequired,
    onSelectAllToggle: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired
        })
    ).isRequired,
    selectedValues: PropTypes.arrayOf(PropTypes.string).isRequired,
    title: PropTypes.string.isRequired,
    onToggleValue: PropTypes.func.isRequired
};

function SingleSelectPanel({ options, selectedValue, title, onSelectValue }) {
    return (
        <div className="journal-filter-popover">
            <div className="journal-filter-popover__header">
                <span className="journal-filter-popover__title">{title}</span>
            </div>
            <div className="journal-filter-popover__list">
                {options.map((option) => {
                    const checked = option.value === selectedValue;

                    return (
                        <button
                            key={option.value}
                            className="journal-filter-popover__option"
                            type="button"
                            onClick={() => onSelectValue(option.value)}
                        >
                            <SelectionIndicator checked={checked} />
                            <span className="truncate">{option.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

SingleSelectPanel.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired
        })
    ).isRequired,
    selectedValue: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    onSelectValue: PropTypes.func.isRequired
};

function TagListPanel({
    selectedTagIds,
    showAllTags,
    tagsByCategory,
    onClear,
    onToggleTag,
    onToggleShowAllTags
}) {
    return (
        <div className="journal-filter-popover">
            <div className="journal-filter-popover__header journal-filter-popover__header--stacked">
                <div className="journal-filter-popover__toggle-row">
                    <span className="text-xs font-medium text-[#525252]">
                        Only show results that contain all selected tags
                    </span>
                    <button
                        className={cn(
                            'journal-filter-toggle',
                            showAllTags && 'journal-filter-toggle--checked'
                        )}
                        type="button"
                        onClick={onToggleShowAllTags}
                    >
                        <span className="journal-filter-toggle__thumb" />
                    </button>
                </div>
                <button className="journal-filter-popover__action" type="button" onClick={onClear}>
                    Clear
                </button>
            </div>
            <div className="journal-filter-popover__list">
                {Object.keys(tagsByCategory).length > 0 ? (
                    Object.entries(tagsByCategory).map(([category, categoryTags]) => (
                        <div key={category} className="space-y-2">
                            <p className="px-2 text-xs font-semibold uppercase tracking-[0.08em] text-[#737373]">
                                {category}
                            </p>
                            <div className="space-y-1">
                                {categoryTags.map((tag) => {
                                    const checked = selectedTagIds.includes(tag._id);

                                    return (
                                        <button
                                            key={tag._id}
                                            className="journal-filter-popover__option"
                                            type="button"
                                            onClick={() => onToggleTag(tag._id)}
                                        >
                                            <SelectionIndicator checked={checked} />
                                            <span className="truncate">{tag.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))
                ) : (
                    <p className="journal-filter-popover__empty">No available tags.</p>
                )}
            </div>
        </div>
    );
}

TagListPanel.propTypes = {
    selectedTagIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    showAllTags: PropTypes.bool.isRequired,
    tagsByCategory: PropTypes.objectOf(
        PropTypes.arrayOf(
            PropTypes.shape({
                _id: PropTypes.string.isRequired,
                label: PropTypes.string.isRequired
            })
        )
    ).isRequired,
    onClear: PropTypes.func.isRequired,
    onToggleTag: PropTypes.func.isRequired,
    onToggleShowAllTags: PropTypes.func.isRequired
};

function PopoverField({ content, label, placeholder, summary }) {
    return (
        <SheetField label={label}>
            <Popover
                arrow={false}
                content={content}
                overlayStyle={{ zIndex: 1305 }}
                overlayClassName="journal-filter-overlay"
                placement="bottomLeft"
                trigger="click"
            >
                <button className="journal-filter-field" type="button">
                    <span
                        className={cn(
                            'truncate text-left text-sm leading-5',
                            summary ? 'text-[#0A0A0A]' : 'text-[#737373]'
                        )}
                    >
                        {summary || placeholder}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 text-[#737373]" />
                </button>
            </Popover>
        </SheetField>
    );
}

PopoverField.propTypes = {
    content: PropTypes.node.isRequired,
    label: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    summary: PropTypes.string
};

PopoverField.defaultProps = {
    placeholder: 'Select',
    summary: ''
};

export default function FilterHeader({ hideDateRange, variant }) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { allAccounts, setSelectedAccount } = useAccountStore((state) => state);
    const { company } = useManagementStore((state) => state);
    const { filter, changeFilter } = useSettingStore((state) => state);
    const { getUser } = useUserState();
    const user = getUser();
    const isViewAsActive = useIsViewAsActive();
    const { data: tags = [] } = useTags();
    const { uniqueSymbols } = useSymbols();
    const { tradingPlans, tradingPlanOptions, hasFetchedTradingPlans } = useTradingPlans();

    const [isSheetMounted, setIsSheetMounted] = useState(false);
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const [draftFilter, setDraftFilter] = useState(() => createFilterDraft(filter));

    const unassignedRouteMatch = pathname.match(/^\/admin\/u\/([^/]+)/i);
    const normalizedUnassignedId = unassignedRouteMatch
        ? unassignedRouteMatch[1].toUpperCase()
        : '';
    const effectiveCompany = getCompanyFromAccountUniqueId(normalizedUnassignedId) || company;

    const tagLookup = useMemo(
        () => Object.fromEntries(tags.map((tag) => [tag._id, tag.label])),
        [tags]
    );

    const tagsByCategory = useMemo(
        () =>
            tags.reduce((accumulator, tag) => {
                const category = tag.categoryLabel || 'Uncategorized';

                if (!accumulator[category]) accumulator[category] = [];

                accumulator[category].push(tag);

                return accumulator;
            }, {}),
        [tags]
    );
    const hasTradingPlanOptions = tradingPlans.length > 0;

    const modifiedFilter = useMemo(
        () => ({
            ...filter,
            dateRange: null,
            symbols: [],
            getAllofTickers: true
        }),
        [filter]
    );

    const canFetchSummary = Array.isArray(filter.accounts) && filter.accounts.length > 0;

    const { data: summary, isFetched } = useQuery({
        queryKey: ['summary-account', modifiedFilter],
        queryFn: () => SUMMARY.fetchSummary(modifiedFilter),
        enabled: canFetchSummary
    });

    useEffect(() => {
        if (summary?.lastTradeDay && summary?.firstTradeDay) {
            const lastTradeDay = dayjs(summary.lastTradeDay);
            const firstTradeDay = dayjs(summary.firstTradeDay);
            const nextLastTradeDay = lastTradeDay.toISOString();
            const nextFirstTradeDay = firstTradeDay.toISOString();
            const cachedLastTradeDay = localStorage.getItem('last-trade-day');
            const cachedFirstTradeDay = localStorage.getItem('first-trade-day');

            if (
                nextFirstTradeDay !== cachedFirstTradeDay ||
                nextLastTradeDay !== cachedLastTradeDay
            ) {
                changeFilter('dateRange', [firstTradeDay, lastTradeDay]);
                localStorage.setItem('last-trade-day', nextLastTradeDay);
                localStorage.setItem('first-trade-day', nextFirstTradeDay);
            }
        } else if (isFetched) {
            changeFilter('dateRange', null);
            localStorage.removeItem('last-trade-day');
            localStorage.removeItem('first-trade-day');
        }
    }, [summary, isFetched, changeFilter]);

    const visibleAccounts = useMemo(() => {
        if (allAccounts.length === 0) return [];

        const companyFilter = COMPANY_ACCOUNT_FILTERS[effectiveCompany];

        if (!companyFilter) return [];

        return allAccounts.filter((account) => {
            const hasValidAccountType =
                !isViewAsActive ||
                account.liveAccount === true ||
                account.simulator_account === true ||
                account.ptt_account ||
                account.pct_account;

            return hasValidAccountType && companyFilter(account);
        });
    }, [allAccounts, effectiveCompany, isViewAsActive]);

    useEffect(() => {
        changeFilter('symbols', uniqueSymbols.length > 0 ? uniqueSymbols : []);
    }, [uniqueSymbols, changeFilter]);

    useEffect(() => {
        if (!isSheetMounted) {
            setDraftFilter(createFilterDraft(filter));
        }
    }, [filter, isSheetMounted]);

    useEffect(() => {
        if (!hasFetchedTradingPlans || hasTradingPlanOptions) return;

        setDraftFilter((previousFilter) => {
            if (
                previousFilter.tradingPlanId === null &&
                previousFilter.strategySetType === null &&
                previousFilter.setNumber === null
            ) {
                return previousFilter;
            }

            return {
                ...previousFilter,
                tradingPlanId: null,
                strategySetType: null,
                setNumber: null
            };
        });

        if (filter.tradingPlanId !== null) {
            changeFilter('tradingPlanId', null);
        }

        if (filter.strategySetType !== null) {
            changeFilter('strategySetType', null);
        }

        if (filter.setNumber !== null) {
            changeFilter('setNumber', null);
        }
    }, [
        changeFilter,
        filter.setNumber,
        filter.strategySetType,
        filter.tradingPlanId,
        hasFetchedTradingPlans,
        hasTradingPlanOptions
    ]);

    useEffect(() => {
        if (!isSheetMounted) return undefined;

        const previousOverflow = document.body.style.overflow;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setIsSheetOpen(false);
            }
        };

        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isSheetMounted]);

    useEffect(() => {
        if (!isSheetMounted || isSheetOpen) return undefined;

        const closeTimer = window.setTimeout(() => {
            setIsSheetMounted(false);
        }, SHEET_TRANSITION_MS);

        return () => window.clearTimeout(closeTimer);
    }, [isSheetMounted, isSheetOpen]);

    const updateDraftFilter = (key, value) => {
        setDraftFilter((previousFilter) => ({
            ...previousFilter,
            [key]: value
        }));
    };

    const toggleDraftArrayValue = (key, value) => {
        setDraftFilter((previousFilter) => {
            const currentValues = Array.isArray(previousFilter[key]) ? previousFilter[key] : [];
            const nextValues = currentValues.includes(value)
                ? currentValues.filter((item) => item !== value)
                : [...currentValues, value];

            return {
                ...previousFilter,
                [key]: nextValues
            };
        });
    };

    const handleOpenSheet = () => {
        setDraftFilter(createFilterDraft(filter));
        setIsSheetMounted(true);
        window.requestAnimationFrame(() => {
            setIsSheetOpen(true);
        });
    };

    const handleCloseSheet = () => {
        setIsSheetOpen(false);
    };

    const handleApplySheet = () => {
        SHEET_FILTER_KEYS.forEach((key) => {
            changeFilter(key, draftFilter[key]);
        });

        setIsSheetOpen(false);
    };

    const handleResetSheet = () => {
        setDraftFilter(createResetDraft(filter, uniqueSymbols));
    };

    const selectedFilterAccountId =
        Array.isArray(filter?.accounts) && filter.accounts.length > 0 ? filter.accounts[0] : null;

    const isFundedAccount =
        allAccounts.filter(
            (account) => account.value === selectedFilterAccountId && account.simulator_account
        ).length > 0;

    const handleImportTrades = () => {
        const availableAccounts = allAccounts.filter(
            (account) => account.label !== 'Live Trade' && !account.simulator_account
        );

        if (!isFundedAccount && availableAccounts.length > 0) {
            setSelectedAccount(availableAccounts[0]);
        }

        navigate('/add-trade');
    };

    const canAccessImportTrades =
        user.role === 'admin' &&
        ['Bear Bull Traders', 'Peak Capital Trading', 'Personal'].includes(company);
    const hasAccounts = visibleAccounts.length > 0;

    const assetSummary = summarizeValues({
        values: draftFilter.assetType,
        labelByValue: Object.fromEntries(
            ASSET_OPTIONS.map((option) => [option.value, option.label])
        ),
        allValues: DEFAULT_ASSET_TYPES,
        maxVisible: 3
    });

    const measureSummary =
        MEASURE_OPTIONS.find((option) => option.value === draftFilter.basic_measure)?.label ||
        '$ - Dollar';

    const tagSummary = summarizeValues({
        values: draftFilter.tags,
        labelByValue: tagLookup
    });

    const tickerSummary = summarizeValues({
        values: draftFilter.symbols,
        labelByValue: Object.fromEntries(uniqueSymbols.map((symbol) => [symbol, symbol])),
        allValues: uniqueSymbols,
        allSelectedLabel:
            uniqueSymbols.length > 3
                ? `All tickers (${uniqueSymbols.length})`
                : uniqueSymbols.join(', '),
        maxVisible: 3
    });

    const transactionsSummary = summarizeValues({
        values: draftFilter.side,
        labelByValue: Object.fromEntries(SIDES.map((side) => [side, side])),
        allValues: DEFAULT_SIDES
    });

    const orderSummary = summarizeValues({
        values: draftFilter.status,
        labelByValue: Object.fromEntries(
            STATUS_OPTIONS.map((option) => [option.value, option.label])
        ),
        allValues: DEFAULT_STATUSES
    });

    if (!hasAccounts) {
        return null;
    }

    return (
        <>
            <div
                className={cn(
                    'journal-filter-header',
                    variant === 'inline' && 'journal-filter-header--inline',
                    variant === 'panel' && 'journal-filter-header--panel'
                )}
            >
                <div className="journal-filter-header__row">
                    <div className="journal-filter-header__controls">
                        <UIButton
                            className="journal-filter-trigger"
                            type="button"
                            variant="outline"
                            onClick={handleOpenSheet}
                        >
                            <ListFilter
                                className="journal-filter-trigger__icon h-4 w-4"
                                strokeWidth={2.1}
                            />
                            <span>Filters</span>
                        </UIButton>

                        {!hideDateRange && (
                            <div className="journal-filter-date">
                                <Calendar className="journal-filter-date__icon" strokeWidth={2.1} />
                                <RangePicker
                                    allowClear={false}
                                    className="journal-filter-range-picker"
                                    format="MMM DD, YYYY"
                                    inputReadOnly
                                    onChange={(range) => changeFilter('dateRange', range)}
                                    popupClassName="journal-filter-calendar-popup"
                                    renderExtraFooter={() => (
                                        <div className="journal-filter-range-presets">
                                            {RANGE_PRESETS.map((preset) => (
                                                <button
                                                    key={preset.label}
                                                    className="journal-filter-range-presets__button"
                                                    type="button"
                                                    onMouseDown={(event) => event.preventDefault()}
                                                    onClick={() =>
                                                        changeFilter('dateRange', preset.value)
                                                    }
                                                >
                                                    {preset.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    separator={
                                        <span className="journal-filter-range-picker__separator">
                                            -
                                        </span>
                                    }
                                    suffixIcon={null}
                                    value={
                                        filter.dateRange
                                            ? [
                                                  dayjs(filter.dateRange[0]),
                                                  dayjs(filter.dateRange[1])
                                              ]
                                            : null
                                    }
                                />
                            </div>
                        )}
                    </div>

                    {canAccessImportTrades && (
                        <UIButton
                            className="journal-filter-import"
                            type="button"
                            onClick={handleImportTrades}
                        >
                            Import
                        </UIButton>
                    )}
                </div>
            </div>

            {isSheetMounted &&
                createPortal(
                    <div
                        className={cn(
                            'journal-filter-sheet-backdrop',
                            isSheetOpen && 'journal-filter-sheet-backdrop--open'
                        )}
                        onClick={handleCloseSheet}
                        role="presentation"
                    >
                        <aside
                            className={cn(
                                'journal-filter-sheet',
                                isSheetOpen && 'journal-filter-sheet--open'
                            )}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="journal-filter-sheet__header">
                                <h2 className="journal-filter-sheet__title">Filters</h2>
                                <button
                                    aria-label="Close filters"
                                    className="journal-filter-sheet__close"
                                    type="button"
                                    onClick={handleCloseSheet}
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>

                            <div className="journal-filter-sheet__divider" />

                            <div className="journal-filter-sheet__body">
                                <div className="journal-filter-sheet__content">
                                    <PopoverField
                                        content={
                                            <SingleSelectPanel
                                                onSelectValue={(value) =>
                                                    updateDraftFilter('basic_measure', value)
                                                }
                                                options={MEASURE_OPTIONS}
                                                selectedValue={
                                                    draftFilter.basic_measure || 'Dollar'
                                                }
                                                title="Currency"
                                            />
                                        }
                                        label="Currency"
                                        summary={measureSummary}
                                    />
                                    <PopoverField
                                        content={
                                            <CheckboxListPanel
                                                allSelected={areArraysEqual(
                                                    draftFilter.assetType,
                                                    DEFAULT_ASSET_TYPES
                                                )}
                                                emptyText="No asset classes available."
                                                onClear={() => updateDraftFilter('assetType', [])}
                                                onSelectAllToggle={() =>
                                                    updateDraftFilter(
                                                        'assetType',
                                                        areArraysEqual(
                                                            draftFilter.assetType,
                                                            DEFAULT_ASSET_TYPES
                                                        )
                                                            ? []
                                                            : [...DEFAULT_ASSET_TYPES]
                                                    )
                                                }
                                                onToggleValue={(value) =>
                                                    toggleDraftArrayValue('assetType', value)
                                                }
                                                options={ASSET_OPTIONS}
                                                selectedValues={draftFilter.assetType}
                                                title="Asset classes"
                                            />
                                        }
                                        label="Asset classes"
                                        summary={assetSummary}
                                    />

                                    <PopoverField
                                        content={
                                            <TagListPanel
                                                onClear={() => {
                                                    updateDraftFilter('tags', []);
                                                    updateDraftFilter('showAllTags', false);
                                                }}
                                                onToggleShowAllTags={() =>
                                                    updateDraftFilter(
                                                        'showAllTags',
                                                        !draftFilter.showAllTags
                                                    )
                                                }
                                                onToggleTag={(tagId) =>
                                                    toggleDraftArrayValue('tags', tagId)
                                                }
                                                selectedTagIds={draftFilter.tags}
                                                showAllTags={draftFilter.showAllTags}
                                                tagsByCategory={tagsByCategory}
                                            />
                                        }
                                        label="Tags"
                                        summary={tagSummary}
                                    />

                                    <PopoverField
                                        content={
                                            <CheckboxListPanel
                                                allSelected={
                                                    uniqueSymbols.length > 0 &&
                                                    areArraysEqual(
                                                        draftFilter.symbols,
                                                        uniqueSymbols
                                                    )
                                                }
                                                emptyText="No available tickers."
                                                onClear={() => updateDraftFilter('symbols', [])}
                                                onSelectAllToggle={() =>
                                                    updateDraftFilter(
                                                        'symbols',
                                                        uniqueSymbols.length > 0 &&
                                                            areArraysEqual(
                                                                draftFilter.symbols,
                                                                uniqueSymbols
                                                            )
                                                            ? []
                                                            : [...uniqueSymbols]
                                                    )
                                                }
                                                onToggleValue={(value) =>
                                                    toggleDraftArrayValue('symbols', value)
                                                }
                                                options={uniqueSymbols.map((symbol) => ({
                                                    label: symbol,
                                                    value: symbol
                                                }))}
                                                selectedValues={draftFilter.symbols}
                                                title="Tickers"
                                            />
                                        }
                                        label="Tickers"
                                        summary={tickerSummary}
                                    />

                                    <PopoverField
                                        content={
                                            <CheckboxListPanel
                                                allSelected={areArraysEqual(
                                                    draftFilter.side,
                                                    DEFAULT_SIDES
                                                )}
                                                emptyText="No transaction filters available."
                                                onClear={() => updateDraftFilter('side', [])}
                                                onSelectAllToggle={() =>
                                                    updateDraftFilter(
                                                        'side',
                                                        areArraysEqual(
                                                            draftFilter.side,
                                                            DEFAULT_SIDES
                                                        )
                                                            ? []
                                                            : [...DEFAULT_SIDES]
                                                    )
                                                }
                                                onToggleValue={(value) =>
                                                    toggleDraftArrayValue('side', value)
                                                }
                                                options={SIDES.map((side) => ({
                                                    label: side,
                                                    value: side
                                                }))}
                                                selectedValues={draftFilter.side}
                                                title="Transactions"
                                            />
                                        }
                                        label="Transactions"
                                        summary={transactionsSummary}
                                    />

                                    <PopoverField
                                        content={
                                            <CheckboxListPanel
                                                allSelected={areArraysEqual(
                                                    draftFilter.status,
                                                    DEFAULT_STATUSES
                                                )}
                                                emptyText="No order filters available."
                                                onClear={() => updateDraftFilter('status', [])}
                                                onSelectAllToggle={() =>
                                                    updateDraftFilter(
                                                        'status',
                                                        areArraysEqual(
                                                            draftFilter.status,
                                                            DEFAULT_STATUSES
                                                        )
                                                            ? []
                                                            : [...DEFAULT_STATUSES]
                                                    )
                                                }
                                                onToggleValue={(value) =>
                                                    toggleDraftArrayValue('status', value)
                                                }
                                                options={STATUS_OPTIONS}
                                                selectedValues={draftFilter.status}
                                                title="Order"
                                            />
                                        }
                                        label="Order"
                                        summary={orderSummary}
                                    />

                                    {hasFetchedTradingPlans && hasTradingPlanOptions && (
                                        <div className="journal-filter-sheet__tradebook">
                                            <TradebookFilters
                                                className="space-y-4"
                                                filterValues={draftFilter}
                                                onFilterChange={updateDraftFilter}
                                                tradingPlanOptions={tradingPlanOptions}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="journal-filter-sheet__divider" />

                                <div className="journal-filter-sheet__footer">
                                    <UIButton
                                        className="journal-filter-sheet__reset"
                                        type="button"
                                        variant="ghost"
                                        onClick={handleResetSheet}
                                    >
                                        Reset
                                    </UIButton>

                                    <div className="journal-filter-sheet__footer-actions">
                                        <UIButton
                                            className="journal-filter-sheet__cancel"
                                            type="button"
                                            variant="outline"
                                            onClick={handleCloseSheet}
                                        >
                                            Cancel
                                        </UIButton>
                                        <UIButton
                                            className="journal-filter-sheet__apply"
                                            type="button"
                                            onClick={handleApplySheet}
                                        >
                                            Apply filters
                                        </UIButton>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>,
                    document.body
                )}
        </>
    );
}

FilterHeader.propTypes = {
    hideDateRange: PropTypes.bool,
    variant: PropTypes.oneOf(['default', 'inline', 'panel'])
};

FilterHeader.defaultProps = {
    hideDateRange: false,
    variant: 'default'
};
